import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Building2, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";

export default function SignupOrg() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const preselectedPlan = params.get("plan");
  const [f, setF] = useState({
    org_name: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => {
    setF({ ...f, [k]: e.target.value });
    if (error) setError(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/orgs/signup", f);
      if (data.access_token) setToken(data.access_token);
      await refresh();
      toast.success(`Välkommen, ${data.user.name}! Organisationen ${f.org_name} är skapad.`);
      if (preselectedPlan) {
        nav(`/billing/plans?plan=${preselectedPlan}`);
      } else {
        nav("/billing/plans");
      }
    } catch (err) {
      const msg = formatApiError(err, "Registreringen misslyckades. Kontrollera uppgifterna och försök igen.");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex bg-zinc-900 text-white p-12 flex-col justify-between"
           style={{ backgroundImage: "linear-gradient(135deg,#0f172a 0%,#1e3a8a 100%)" }}>
        <div>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1 rounded-sm text-xs uppercase tracking-wider">
            <Building2 className="w-3.5 h-3.5" /> Registrera kommun
          </div>
          <h1 className="mt-8 text-5xl font-semibold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>
            Hela bygglovs&shy;processen.
            <br />
            <span className="text-blue-300">Digital.</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-md text-lg leading-relaxed">
            Skapa ert konto på 2 minuter. 14 dagars gratis trial — utan kreditkort.
            Välj plan efter att ni testat plattformen.
          </p>
        </div>
        <div className="space-y-3 text-sm text-white/60">
          <div>✓ Svensk hosting & GDPR-trygg</div>
          <div>✓ BankID-inloggning för sökande</div>
          <div>✓ AI-assistent enligt PBL</div>
          <div>✓ Tillsynsmodul (15 steg)</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12 bg-zinc-50">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Skapa kommunkonto
          </h2>
          <p className="mt-2 text-sm text-zinc-600">
            Du blir organisationens ägare och första handläggare.
          </p>

          {error && (
            <div
              data-testid="signup-error"
              className="mt-5 flex items-start gap-2 border border-red-200 bg-red-50 text-red-800 rounded-sm px-3 py-2.5 text-sm"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 stroke-[1.75]" />
              <div>{error}</div>
            </div>
          )}

          <form onSubmit={submit} className="mt-7 space-y-3" noValidate>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Kommun / Organisation</label>
              <input value={f.org_name} onChange={set("org_name")} required data-testid="org-name"
                placeholder="t.ex. Solna stad" autoFocus
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 bg-white rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Ditt namn (administratör)</label>
              <input value={f.admin_name} onChange={set("admin_name")} required data-testid="admin-name"
                placeholder="För- och efternamn"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 bg-white rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Jobb-e-post</label>
              <input type="email" value={f.admin_email} onChange={set("admin_email")} required data-testid="admin-email"
                placeholder="namn@kommun.se"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 bg-white rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Telefon (valfritt)</label>
              <input value={f.phone} onChange={set("phone")} data-testid="admin-phone"
                placeholder="08-..."
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 bg-white rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Lösenord</label>
              <input type="password" value={f.admin_password} onChange={set("admin_password")} required
                minLength={8} data-testid="admin-password"
                placeholder="Minst 8 tecken"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 bg-white rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>

            <button type="submit" disabled={submitting} data-testid="signup-submit"
              className="mt-5 w-full px-4 py-3 bg-blue-700 hover:bg-blue-800 text-white rounded-sm text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Skapa konto & välj plan <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-xs text-zinc-500 text-center">
            Genom att registrera er godkänner ni våra villkor. Avsluta närsomhelst.
          </p>
          <p className="mt-4 text-sm text-zinc-600 text-center">
            Redan kund? <Link to="/login" className="text-blue-700 hover:underline font-medium">Logga in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
